import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { formatApiError, assetUrl } from "../../lib/api";
import SEO from "../../components/site/SEO";
import { inputCls, labelCls, btnPrimaire, btnSecondaire, btnDanger, dateFr, confirmerSuppression } from "./ui";

const VIDE = { titre: "", description: "", prix: "", texte_bandeau: "", photo: "", date_debut: "", date_fin: "", bandeau: false, actif: true };

export default function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [edition, setEdition] = useState(null); // null = liste, {} = formulaire
  const [photos, setPhotos] = useState([]);

  const charger = () => api.get("/admin/promotions").then((r) => setPromos(r.data)).catch(() => {});
  useEffect(() => {
    charger();
    api.get("/admin/photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const enregistrer = async (e) => {
    e.preventDefault();
    try {
      if (edition.id) {
        await api.put(`/admin/promotions/${edition.id}`, edition);
        toast.success("Votre promotion a été modifiée.");
      } else {
        await api.post("/admin/promotions", edition);
        toast.success("Votre promotion est en ligne.");
      }
      setEdition(null);
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const basculer = async (p) => {
    await api.put(`/admin/promotions/${p.id}`, { ...p, actif: !p.actif });
    toast.success(p.actif ? "Promotion masquée du site." : "Votre promotion est en ligne.");
    charger();
  };

  const supprimer = async (p) => {
    if (!confirmerSuppression(p.titre)) return;
    await api.delete(`/admin/promotions/${p.id}`);
    toast.success("Promotion supprimée.");
    charger();
  };

  if (edition) {
    return (
      <div className="max-w-2xl" data-testid="promo-formulaire">
        <SEO title="Promotion — Espace gérant | Promo Pneus" description="Créer ou modifier une promotion." />
        <h1 className="font-heading font-bold uppercase text-3xl text-brand-dark mb-2">
          {edition.id ? "Modifier la promotion" : "Ajouter une promotion"}
        </h1>
        <p className="text-brand-body mb-6 bg-brand-light border-l-4 border-brand-red px-4 py-3">
          La promotion disparaîtra du site automatiquement après la date de fin. Vous n'avez rien à faire.
        </p>
        <form onSubmit={enregistrer} className="space-y-5 bg-white p-6 shadow-sm">
          <div>
            <label htmlFor="promo-titre" className={labelCls}>Titre court</label>
            <input id="promo-titre" data-testid="promo-titre" required maxLength={80} className={inputCls}
              value={edition.titre} onChange={(e) => setEdition({ ...edition, titre: e.target.value })}
              placeholder="Ex. : 4 pneus achetés, l'équilibrage offert" />
          </div>
          <div>
            <label htmlFor="promo-description" className={labelCls}>Description</label>
            <textarea id="promo-description" data-testid="promo-description" rows={3} className={inputCls}
              value={edition.description} onChange={(e) => setEdition({ ...edition, description: e.target.value })}
              placeholder="Expliquez l'offre en une ou deux phrases." />
          </div>
          <div>
            <label htmlFor="promo-prix" className={labelCls}>Prix ou remise (facultatif)</label>
            <input id="promo-prix" data-testid="promo-prix" className={inputCls}
              value={edition.prix} onChange={(e) => setEdition({ ...edition, prix: e.target.value })}
              placeholder="Ex. : -20 % ou 59 €" />
          </div>
          <div>
            <label htmlFor="promo-bandeau-texte" className={labelCls}>Texte du bandeau (court)</label>
            <input id="promo-bandeau-texte" data-testid="promo-bandeau-texte" maxLength={45} className={inputCls}
              value={edition.texte_bandeau || ""} onChange={(e) => setEdition({ ...edition, texte_bandeau: e.target.value })}
              placeholder="Ex. : 4 pneus achetés, l'équilibrage offert" />
            <p className="text-sm text-brand-body mt-1 flex justify-between gap-3">
              <span>Une phrase très courte, visible en haut du site sur mobile.</span>
              <span data-testid="promo-bandeau-compteur" className="shrink-0 font-semibold">{(edition.texte_bandeau || "").length}/45</span>
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="promo-debut" className={labelCls}>Date de début</label>
              <input id="promo-debut" data-testid="promo-debut" type="date" required className={inputCls}
                value={edition.date_debut} onChange={(e) => setEdition({ ...edition, date_debut: e.target.value })} />
            </div>
            <div>
              <label htmlFor="promo-fin" className={labelCls}>Date de fin</label>
              <input id="promo-fin" data-testid="promo-fin" type="date" required className={inputCls}
                value={edition.date_fin} onChange={(e) => setEdition({ ...edition, date_fin: e.target.value })} />
            </div>
          </div>
          <div>
            <label htmlFor="promo-photo" className={labelCls}>Photo (facultatif)</label>
            <select id="promo-photo" data-testid="promo-photo" className={inputCls}
              value={edition.photo} onChange={(e) => setEdition({ ...edition, photo: e.target.value })}>
              <option value="">Aucune photo</option>
              {photos.map((ph) => <option key={ph.id} value={ph.url}>{ph.nom}</option>)}
            </select>
            <p className="text-sm text-brand-body mt-2">Pour ajouter une photo, allez d'abord dans l'onglet Photos.</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="promo-bandeau" className="mt-1 w-6 h-6 accent-[#E8492F]"
              checked={edition.bandeau} onChange={(e) => setEdition({ ...edition, bandeau: e.target.checked })} />
            <span className="font-body text-brand-dark text-base">Afficher aussi dans le bandeau en haut du site</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button type="submit" data-testid="promo-enregistrer" className={btnPrimaire}>
              {edition.id ? "Enregistrer les modifications" : "Mettre en ligne"}
            </button>
            <button type="button" data-testid="promo-annuler" onClick={() => setEdition(null)} className={btnSecondaire}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-promotions">
      <SEO title="Promotions — Espace gérant | Promo Pneus" description="Gérer les promotions du site." />
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <h1 className="font-heading font-bold uppercase text-4xl text-brand-dark tracking-tight">Promotions</h1>
        <button data-testid="promo-ajouter" onClick={() => setEdition(VIDE)} className={`${btnPrimaire} inline-flex items-center gap-2`}>
          <Plus size={22} aria-hidden="true" /> Ajouter une promotion
        </button>
      </div>
      {promos.length === 0 && (
        <p className="bg-white p-8 text-brand-body text-lg shadow-sm" data-testid="promo-vide">
          Aucune promotion pour le moment. Cliquez sur « Ajouter une promotion » pour en créer une.
        </p>
      )}
      <div className="space-y-4">
        {promos.map((p) => (
          <div key={p.id} data-testid={`promo-ligne-${p.id}`} className="bg-white p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4">
            {p.photo && <img src={assetUrl(p.photo)} alt="" className="w-24 h-16 object-cover shrink-0" loading="lazy" />}
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold uppercase text-xl text-brand-dark">{p.titre}</p>
              <p className="text-brand-body text-sm">
                Du {dateFr(p.date_debut)} au {dateFr(p.date_fin)}
                {p.bandeau ? " · affichée dans le bandeau" : ""}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                data-testid={`promo-bascule-${p.id}`}
                onClick={() => basculer(p)}
                className={`font-heading font-semibold uppercase tracking-wider px-5 py-3 text-base transition-colors ${
                  p.actif ? "bg-green-700 text-white" : "bg-gray-300 text-brand-dark"
                }`}
                aria-pressed={p.actif}
              >
                {p.actif ? "Active" : "Inactive"}
              </button>
              <button data-testid={`promo-modifier-${p.id}`} onClick={() => setEdition(p)}
                className="p-3 bg-brand-light hover:bg-gray-200 text-brand-dark transition-colors" aria-label={`Modifier ${p.titre}`}>
                <Pencil size={22} />
              </button>
              <button data-testid={`promo-supprimer-${p.id}`} onClick={() => supprimer(p)}
                className="p-3 bg-red-50 hover:bg-red-100 text-brand-redhover transition-colors" aria-label={`Supprimer ${p.titre}`}>
                <Trash2 size={22} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
